import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import type { RootState } from '../../../../configuration/setup/store';
import { getAssetDiscounts } from '../../common/discount/redux/discount.redux';
import type { AssetDiscount } from '../../common/discount/redux/types';
import { isFreeTrialLevel } from '../../common/termedLevel/freeTrialLevel';
import { isIntegratedOfferLevel } from '../../common/termedLevel/integratedOfferLevel';
import {
    type BookableProduct,
    type CheckoutState,
    isBookableProduct,
    isMultiLevelProduct,
    type Product,
    type ProductLevel,
} from './types';

const initialState: CheckoutState = {
    selectedProductId: undefined,
    selectedProduct: undefined,
    selectedProductLevel: undefined,
    locked: false,
    internalCampaign: undefined,
};

export const { reducer: checkoutReducer, actions: checkoutActions } = createSlice({
    name: 'checkout',
    initialState,
    reducers: {
        setSelectedProductId: (state: CheckoutState, action: PayloadAction<string | undefined>) => {
            state.selectedProductId = action.payload;
        },
        setSelectedProduct: (state: CheckoutState, action: PayloadAction<Product | undefined>) => {
            state.selectedProduct = action.payload;
        },
        setSelectedProductLevel: (state: CheckoutState, action: PayloadAction<string | undefined>) => {
            state.selectedProductLevel = action.payload;
        },
        setInternalCampaign: (state: CheckoutState, action: PayloadAction<string | undefined>) => {
            state.internalCampaign = action.payload;
        },
        resetSelectedProductState: (state: CheckoutState) => {
            state.selectedProductId = undefined;
            state.selectedProduct = undefined;
            state.selectedProductLevel = undefined;
        },
        setCheckoutIsLocked: (state: CheckoutState, action: PayloadAction<boolean>) => {
            state.locked = action.payload;
        },
    },
});

const getBase = (state: RootState) => state.marketplace.checkout;

export const getSelectedProduct = (state: RootState) => getBase(state).selectedProduct;
export const getSelectedProductWithoutFTandIO = createSelector(
    [getSelectedProduct],
    (selectedProduct): Product | undefined => {
        if (!isMultiLevelProduct(selectedProduct)) {
            return selectedProduct;
        }
        return {
            ...selectedProduct,
            levels: selectedProduct.levels.filter(
                productLevel => !isFreeTrialLevel(productLevel.level) && !isIntegratedOfferLevel(productLevel.level)
            ),
        };
    },
    {
        // Returning an unchanged regular product is intentional; only multi-level products need filtering.
        devModeChecks: { identityFunctionCheck: 'never' },
    }
);
export const getSelectedProductType = (state: RootState) => getSelectedProduct(state)?.productType;
export const getSelectedProductLevel = (state: RootState) => getBase(state).selectedProductLevel;
export const getSelectedProductId = (state: RootState) => getBase(state).selectedProductId;
export const getInternalCampaign = (state: RootState) => getBase(state).internalCampaign;
export const getIsCheckoutLocked = (state: RootState) => getBase(state).locked;
export const getSelectedVariant = (state: RootState) => getLevelOfProduct(state)?.variantName;
export const getLevelOfProduct = (state: RootState): ProductLevel | undefined => {
    const selectedProduct = getSelectedProduct(state);
    const selectedProductLevel = getSelectedProductLevel(state);
    if (!isMultiLevelProduct(selectedProduct)) {
        return;
    }
    return selectedProduct.levels.find(level => level.level === selectedProductLevel);
};
export const getRenewsToProductLevel = createSelector(
    getSelectedProduct,
    getLevelOfProduct,
    (selectedProduct, levelOfProduct): ProductLevel | undefined => {
        if (!isMultiLevelProduct(selectedProduct) || !levelOfProduct?.renewsToLevel) {
            return;
        }
        return selectedProduct.levels.find(level => level.level === levelOfProduct.renewsToLevel);
    }
);
export const getSelectedProductOrProductLevel = createSelector(
    getSelectedProduct,
    getLevelOfProduct,
    (selectedProduct, levelOfProduct): BookableProduct => {
        if (isMultiLevelProduct(selectedProduct)) {
            if (!levelOfProduct) {
                throw Error(`Selected level not found for ${JSON.stringify(selectedProduct)}.`);
            }
            return {
                sku: selectedProduct.sku,
                productType: selectedProduct.productType,
                name: levelOfProduct.name,
                price: levelOfProduct.price,
                currency: levelOfProduct.currency,
                ratePlanId: levelOfProduct.ratePlanId,
                level: levelOfProduct.level,
                contractPeriod: levelOfProduct.contractPeriod,
                variantName: levelOfProduct.variantName,
                renewsToLevel: levelOfProduct.renewsToLevel,
                effectiveEndDate: levelOfProduct.effectiveEndDate,
                futurePrice: levelOfProduct.futurePrice,
            };
        }
        if (!isBookableProduct(selectedProduct)) {
            throw Error(`Selected product ${selectedProduct} is not bookable.`);
        }
        return selectedProduct;
    }
);

export const getProductLevelsForVariant = (state: RootState): ProductLevel[] | undefined => {
    const selectedProduct = getSelectedProductWithoutFTandIO(state);
    const selectedVariant = getSelectedVariant(state);
    if (!isMultiLevelProduct(selectedProduct)) {
        return;
    }
    if (selectedVariant) {
        const levelsForVariant = selectedProduct.levels.filter(
            productLevel => productLevel.variantName === selectedVariant && !productLevel.legacy
        );
        if (levelsForVariant.length > 1) {
            return levelsForVariant;
        }
    }
    return;
};

export const getFullyDiscountedLevel = (state: RootState): string | undefined => {
    const assetDiscounts = getAssetDiscounts(state);
    try {
        const selectedProduct = getSelectedProduct(state);
        if (!isMultiLevelProduct(selectedProduct)) {
            return;
        }
        const matchesProduct = (discount: AssetDiscount) =>
            discount.productId.toLowerCase() === selectedProduct.sku.toLowerCase();
        const matchesPercentage = (discount: AssetDiscount) => discount.discountPercentage === 100;
        const matchesContractPeriod = (discount: AssetDiscount) => {
            const matchesProductLevel = selectedProduct.levels.find(productLevel =>
                discount.productLevels?.includes(productLevel.level)
            );
            if (matchesProductLevel?.contractPeriod) {
                return (
                    matchesProductLevel.contractPeriod.length === discount.durationPeriod &&
                    matchesProductLevel.contractPeriod.unit.toString().startsWith(discount.durationUnit.toString())
                );
            }
            return;
        };
        const matchesUnappliedDiscount = (discount: AssetDiscount) => discount.endsAt === undefined;
        const relevantAssetDiscounts = assetDiscounts
            .filter(matchesUnappliedDiscount)
            .filter(matchesProduct)
            .filter(matchesPercentage)
            .filter(matchesContractPeriod);
        if (relevantAssetDiscounts.length !== 0) {
            return relevantAssetDiscounts[0].productLevels?.at(0);
        }
    } catch {
        //setSelectedProduct is called after VariantSelectionContainer is rendered
    }
    return;
};
